import { useAccessLinkEditMode } from '@confluence/external-share-context/entry-points/AccessLinkEditMode.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

/**
 * Whether an editor is open, by any of the ways Confluence opens one.
 *
 * Use this rather than `useIsEditorPage()` when deciding whether to suppress a page-scoped
 * keyboard shortcut, navigation affordance, or piece of view-page chrome. Only one of the three
 * cases involves an edit route:
 *
 *   1. classic edit routes — `useIsEditorPage()`
 *   2. a live document, which mounts the editor on the view route — `useLivePageMode()`
 *   3. an Access Link editing in place, which cannot change the URL because it carries the share
 *      token authorising the session — `useAccessLinkEditMode()`
 *
 * Case 3's `isAvailable` term is false off that route, so this behaves exactly as
 * `isLiveEditMode || isOnEditRoute` did everywhere else.
 */
export const useIsEditorOpen = (): boolean => {
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();
	const { isAvailable: isAccessLinkEditAvailable, isEditMode: isAccessLinkEditing } =
		useAccessLinkEditMode();

	const isEditorOpen =
		isLiveEditMode || isOnEditRoute || (isAccessLinkEditAvailable && isAccessLinkEditing);

	return isEditorOpen;
};
